import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { apiDraftsApi } from '../../utils/api';
import {
  FileText, Check, XCircle, RefreshCw, Search,
  Clock, CheckCircle, Zap, X, Loader2, Eye, User, Undo2, Settings, Mail, Send, Save, Trash2, AlertTriangle
} from 'lucide-react';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import useAuthStore from '../../store/authStore';
import { documentsApi } from '../../utils/api';
import DraggableModal from '../../components/DraggableModal';
import InvoiceTemplate from '../../components/InvoiceTemplate';
import ConfirmModal from '../../components/ConfirmModal';
import MonthRangePicker from '../../components/MonthRangePicker';
import useToolbarResponsive from '../../hooks/useToolbarResponsive';

import useFiltroPeriodo from '../../hooks/useFiltroPeriodo';
import RevisionFacturaApiModal from './RevisionFacturaApiModal';
// Estado del documento dentro del flujo de Facturación API (NO es el estado de Hacienda)
const FLUJO_ESTADO = {
  borrador:  { label: 'Borrador',  color: 'bg-gray-100 text-gray-600' },
  aprobado:  { label: 'Aprobado',  color: 'bg-blue-100 text-blue-700' },
  facturado: { label: 'Facturado', color: 'bg-green-100 text-green-700' },
  rechazado: { label: 'Rechazado', color: 'bg-red-100 text-red-700' }
};

// Deriva la etapa del ciclo de vida del documento API según su revisión y si ya se facturó.
function getEstadoFlujo(doc) {
  if (doc.apiReviewStatus === 'rejected') return 'rechazado';
  if (doc.apiReviewStatus !== 'approved') return 'borrador';
  // Aprobado: si ya salió de borrador (se mandó a facturar) => facturado
  if (doc.processedAt || (doc.estadoHacienda && doc.estadoHacienda !== 'borrador')) return 'facturado';
  return 'aprobado';
}

const HACIENDA_STATUS = {
  borrador: { label: 'Sin enviar', color: 'bg-gray-100 text-gray-500' },
  pendiente: { label: 'Enviado', color: 'bg-blue-100 text-blue-700' },
  procesando: { label: 'Procesando', color: 'bg-blue-100 text-blue-700' },
  aceptado: { label: 'Aceptado', color: 'bg-green-100 text-green-700' },
  rechazado: { label: 'Rechazado', color: 'bg-red-100 text-red-700' },
  error: { label: 'Error', color: 'bg-red-100 text-red-700' }
};

// Reuse SSE progress modal (same as recurrentes)
function ProgresoModal({ isOpen, onClose, resultados, progreso, total, stage, completado }) {
  if (!isOpen) return null;
  const porcentaje = total > 0 ? Math.round((progreso / total) * 100) : 0;
  const exitosos = resultados.filter(r => r.success).length;
  const fallidos = resultados.filter(r => !r.success).length;
  const STAGES = {
    loading: { label: 'Cargando factura', icon: FileText, color: 'text-blue-600' },
    generating: { label: 'Generando XML', icon: FileText, color: 'text-purple-600' },
    signing: { label: 'Firmando documento', icon: Check, color: 'text-indigo-600' },
    submitting: { label: 'Enviando a Hacienda', icon: Zap, color: 'text-orange-600' },
    validating: { label: 'Validando respuesta', icon: RefreshCw, color: 'text-cyan-600' }
  };
  const currentStage = stage?.stage ? STAGES[stage.stage] : null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 bg-gray-900/60" />
        <div className="relative w-full max-w-5xl bg-white rounded-xl border shadow-xl overflow-hidden">
          <div className="px-6 py-4 border-b">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg border ${completado ? 'border-green-300 bg-green-50' : 'border-blue-300 bg-blue-50'}`}>
                {completado ? <CheckCircle className="w-6 h-6 text-green-600" /> : <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold">{completado ? 'Facturación Completada' : 'Procesando Facturas'}</h3>
                <p className="text-sm text-gray-500">{completado ? `${exitosos} exitosas, ${fallidos} con error` : `Factura ${progreso} de ${total}`}</p>
              </div>
              {completado && <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg border"><X className="w-5 h-5 text-gray-500" /></button>}
            </div>
          </div>
          <div className="p-6">
            <div className="mb-6">
              <div className="flex justify-between text-sm mb-2">
                <span className="font-medium text-gray-700">Progreso</span>
                <span className="font-semibold">{porcentaje}%</span>
              </div>
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden border">
                <div className={`h-full transition-all duration-500 rounded-full ${completado ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${porcentaje}%` }} />
              </div>
            </div>
            {!completado && currentStage && (
              <div className="mb-6 p-4 bg-white rounded-lg border">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`p-2 rounded-lg border ${currentStage.color}`}><currentStage.icon className="w-5 h-5" /></div>
                  <div>
                    <p className="font-semibold">{stage?.cliente || 'Procesando...'}</p>
                    <p className="text-sm text-gray-500">{currentStage.label}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 mt-3">
                  {Object.entries(STAGES).map(([key], idx) => {
                    const currentIdx = Object.keys(STAGES).indexOf(stage?.stage);
                    return <div key={key} className="flex-1"><div className={`h-1.5 rounded-full ${idx < currentIdx ? 'bg-green-500' : key === stage?.stage ? 'bg-blue-500' : 'bg-gray-200'}`} /></div>;
                  })}
                </div>
              </div>
            )}
            {resultados.length > 0 && (
              <div className="border rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-gray-50 border-b">
                    <tr><th className="px-3 py-2 text-left">Consecutivo</th><th className="px-3 py-2 text-left">Cliente</th><th className="px-3 py-2 text-center">Estado</th></tr>
                  </thead>
                  <tbody className="divide-y">
                    {resultados.map((r, i) => (
                      <Fragment key={i}>
                        <tr className={r.success ? 'hover:bg-green-50/50' : 'bg-red-50/40'}>
                          <td className="px-3 py-2"><code className="text-[10px] font-mono">{r.consecutivo || '—'}</code></td>
                          <td className="px-3 py-2 font-medium">{r.cliente}</td>
                          <td className="px-3 py-2 text-center">
                            {r.success ? (
                              <span className="px-2 py-0.5 rounded text-[10px] font-semibold border border-green-300 text-green-700">{r.estado === 'aceptado' ? 'Aceptado' : 'Procesando'}</span>
                            ) : (
                              <span className="px-2 py-0.5 rounded text-[10px] font-semibold border border-red-300 text-red-700">Error</span>
                            )}
                          </td>
                        </tr>
                        {!r.success && r.error && (
                          <tr className="bg-red-50/40">
                            <td colSpan={3} className="px-3 pb-2 pt-0">
                              <div className="flex items-start gap-1.5 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">
                                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                                <span className="whitespace-pre-line leading-snug">{r.error}</span>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {completado && <div className="mt-6 flex justify-end"><button onClick={onClose} className="px-6 py-2.5 border rounded-lg hover:bg-gray-50">Cerrar</button></div>}
          </div>
        </div>
      </div>
    </div>
  );
}

// Modal pequeño con el seguimiento (timeline) del documento dentro del flujo API
function TrackingModal({ doc, onClose }) {
  if (!doc) return null;
  const estado = getEstadoFlujo(doc);
  const fmt = (d) => d ? dayjs(d).format('DD/MM/YYYY HH:mm') : null;

  let steps;
  if (estado === 'rechazado') {
    steps = [
      { label: 'Borrador', desc: 'Recibido por API', date: fmt(doc.createdAt), done: true, icon: FileText, color: 'text-gray-500' },
      { label: 'Rechazado', desc: doc.apiReviewReason || 'Sin motivo registrado', date: fmt(doc.apiReviewedAt), done: true, icon: XCircle, color: 'text-red-600', user: doc.aprobadoPor }
    ];
  } else {
    const facturado = estado === 'facturado';
    const aprobado = facturado || estado === 'aprobado';
    steps = [
      { label: 'Borrador', desc: 'Recibido por API', date: fmt(doc.createdAt), done: true, icon: FileText, color: 'text-gray-500' },
      { label: 'Aprobado', desc: 'Listo para facturar', date: fmt(doc.apiReviewedAt), done: aprobado, icon: Check, color: 'text-blue-600', user: aprobado ? doc.aprobadoPor : null },
      { label: 'Facturado', desc: 'Enviado a facturación', date: fmt(doc.processedAt), done: facturado, icon: Zap, color: 'text-green-600', user: facturado ? doc.facturadoPor : null }
    ];
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="fixed inset-0 bg-gray-900/50" onClick={onClose} />
      <div className="relative w-full max-w-sm bg-white rounded-xl border shadow-xl">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-800">Seguimiento del documento</h3>
            <p className="text-xs text-gray-500 truncate">{doc.consecutivo || 'Borrador'} · {doc.receptorNombre}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg shrink-0"><X className="w-4 h-4 text-gray-500" /></button>
        </div>
        <div className="p-5">
          <ol className="relative">
            {steps.map((s, i) => {
              const Icon = s.icon;
              const isLast = i === steps.length - 1;
              return (
                <li key={i} className="relative pl-9 pb-5 last:pb-0">
                  {!isLast && <span className={`absolute left-3 top-7 -bottom-1 w-0.5 ${s.done ? 'bg-gray-300' : 'bg-gray-200'}`} />}
                  <span className={`absolute left-0 top-0 flex items-center justify-center w-7 h-7 rounded-full border-2 bg-white ${s.done ? s.color + ' border-current' : 'border-gray-200 text-gray-300'}`}>
                    <Icon className="w-3.5 h-3.5" />
                  </span>
                  <p className={`text-sm font-medium ${s.done ? 'text-gray-800' : 'text-gray-400'}`}>{s.label}</p>
                  <p className="text-xs text-gray-500">{s.desc}</p>
                  <p className={`text-xs mt-0.5 ${s.done ? 'text-gray-600 font-medium' : 'text-gray-300'}`}>{s.date || 'Pendiente'}</p>
                  {s.user && <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5"><User className="w-3 h-3" />{s.user}</p>}
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </div>
  );
}

export default function FacturacionApi() {
  const { currentEmisor } = useAuthStore();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  // Filtro de período unificado (MonthRangePicker). Default = mes actual.
  const [filtroFecha, setFiltroFecha] = useFiltroPeriodo('FacturacionApi', () => {
    const n = new Date();
    const y = n.getFullYear();
    const m = String(n.getMonth() + 1).padStart(2, '0');
    const last = new Date(y, n.getMonth() + 1, 0).getDate();
    return {
      modo: 'mes',
      periodo: `${y}-${m}`,
      desde: `${y}-${m}-01`,
      hasta: `${y}-${m}-${String(last).padStart(2, '0')}`
    };
  });
  // Toolbar responsive — empuja los botones a la derecha si el search los traslapa
  const { searchRef, buttonsRef } = useToolbarResponsive([filtroFecha]);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  // Pestaña activa: separa las FE válidas (por facturar) de las rechazadas por revisión API
  const [activeTab, setActiveTab] = useState('validas'); // 'validas' | 'rechazadas'
  const [showAlertConfig, setShowAlertConfig] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [trackingDoc, setTrackingDoc] = useState(null);
  // Revisión de Finance 360 (catálogo y mínimos de Hacienda): { doc, rechazo }
  const [revision, setRevision] = useState(null);
  const [confirmarLote, setConfirmarLote] = useState(null);

  // SSE Progress
  const [showProgresoModal, setShowProgresoModal] = useState(false);
  const [progreso, setProgreso] = useState(0);
  const [totalProcesar, setTotalProcesar] = useState(0);
  const [stageInfo, setStageInfo] = useState(null);
  const [resultados, setResultados] = useState([]);
  const [completado, setCompletado] = useState(false);
  const eventSourceRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status: 'all', limit: 500 };
      if (filtroFecha?.desde) params.desde = filtroFecha.desde;
      if (filtroFecha?.hasta) params.hasta = filtroFecha.hasta;
      const res = await apiDraftsApi.list(params);
      setDocs(res.data.data || []);
    } catch (error) {
      toast.error('Error al cargar documentos');
    } finally {
      setLoading(false);
    }
  }, [filtroFecha]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredDocs = docs.filter(d => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (d.receptorNombre || '').toLowerCase().includes(q) || (d.consecutivo || '').includes(q);
  });

  const pendingDocs = filteredDocs.filter(d => d.apiReviewStatus === 'pending_review');
  const approvedDocs = filteredDocs.filter(d => d.apiReviewStatus === 'approved' && d.estadoHacienda === 'borrador');

  // Separación por pestaña: "Rechazadas" agrupa las rechazadas por revisión API
  // y las eliminadas (soft delete). "Válidas" es el resto (por facturar/facturadas).
  const rejectedDocs = filteredDocs.filter(d => d.apiReviewStatus === 'rejected' || d.apiDeleted);
  const validDocs = filteredDocs.filter(d => d.apiReviewStatus !== 'rejected' && !d.apiDeleted);
  const tabDocs = activeTab === 'rechazadas' ? rejectedDocs : validDocs;

  const switchTab = (t) => { setActiveTab(t); setSelectedIds([]); };

  // Approve (mark for facturación)
  // Revierte una aprobación: solo válido si el doc aún está en borrador (no facturado)
  const [confirmDesaprobarId, setConfirmDesaprobarId] = useState(null);
  const confirmarDesaprobar = async () => {
    if (!confirmDesaprobarId) return;
    setProcessing(true);
    try {
      await apiDraftsApi.unapprove(confirmDesaprobarId);
      toast.success('Documento desaprobado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al desaprobar');
    } finally {
      setProcessing(false);
    }
  };

  // Eliminar rechazados (batch). Solo permite eliminar docs con apiReviewStatus='rejected'.
  // El backend (Documento.delete) ya valida que solo borre si estadoHacienda='borrador',
  // y los rechazados por revisión API siempre están en borrador (nunca llegaron a Hacienda).
  const [confirmDeleteIds, setConfirmDeleteIds] = useState(null);
  const confirmarEliminar = async () => {
    if (!confirmDeleteIds || confirmDeleteIds.length === 0) return;
    setProcessing(true);
    try {
      // Soft delete: no se borra de la BD; se marca como eliminada y queda visible
      // en la pestaña "Rechazadas" (se puede restaurar).
      const res = await apiDraftsApi.softDelete(confirmDeleteIds);
      const ok = res.data?.deleted ?? confirmDeleteIds.length;
      toast.success(`${ok} documento(s) eliminado(s)`);
      setSelectedIds([]);
      setConfirmDeleteIds(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al eliminar');
    } finally {
      setProcessing(false);
    }
  };

  // Restaurar un documento soft-deleted (lo saca de "eliminada", vuelve a su estado)
  const handleRestore = async (id) => {
    setProcessing(true);
    try {
      await apiDraftsApi.restore(id);
      toast.success('Documento restaurado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al restaurar');
    } finally {
      setProcessing(false);
    }
  };

  const handleAprobar = async (directIds = null) => {
    const idsToUse = directIds || selectedIds;
    const toApprove = idsToUse.filter(id => {
      const doc = docs.find(d => d.id === id);
      return doc?.apiReviewStatus === 'pending_review';
    });
    if (toApprove.length === 0) return;
    const conObs = toApprove.filter(id => (docs.find(d => d.id === id)?.revisionObservaciones || 0) > 0);
    if (conObs.length && !directIds?.confirmado) { setConfirmarLote({ ids: toApprove, conObs: conObs.length }); return; }
    setProcessing(true);
    try {
      await apiDraftsApi.batchApprove(toApprove);
      toast.success(`${toApprove.length} documento(s) aprobados`);
      setSelectedIds([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al aprobar');
    } finally {
      setProcessing(false);
    }
  };

  // Facturar via SSE (same as recurrentes)
  const handleFacturar = () => {
    // Use selected approved docs, or all approved if none selected
    const selectedApproved = selectedIds.filter(id => {
      const doc = docs.find(d => d.id === id);
      return doc?.estadoHacienda === 'borrador';
    });
    const docsToProcess = selectedApproved.length > 0 ? selectedApproved : approvedDocs.map(d => d.id);

    if (docsToProcess.length === 0) {
      toast.error('No hay documentos aprobados para facturar');
      return;
    }

    setProgreso(0);
    setTotalProcesar(docsToProcess.length);
    setStageInfo(null);
    setResultados([]);
    setCompletado(false);
    setShowProgresoModal(true);

    const ids = docsToProcess.join(',');
    const token = localStorage.getItem('token');
    const emisorId = currentEmisor?.id;
    const apiUrl = import.meta.env.VITE_API_URL || '';

    eventSourceRef.current = new EventSource(
      `${apiUrl}/internal/recurring/generate-batch?ids=${ids}&token=${encodeURIComponent(token)}&emisor_id=${emisorId}`
    );

    eventSourceRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'start') setTotalProcesar(data.total);
      else if (data.type === 'stage') { setProgreso(data.current); setStageInfo({ stage: data.stage, cliente: data.cliente, message: data.message }); }
      else if (data.type === 'result') setResultados(prev => [...prev, data]);
      else if (data.type === 'complete') { setCompletado(true); setStageInfo(null); eventSourceRef.current?.close(); setSelectedIds([]); fetchData(); }
      else if (data.type === 'error') { toast.error(data.error); eventSourceRef.current?.close(); setCompletado(true); }
    };

    eventSourceRef.current.onerror = () => { eventSourceRef.current?.close(); if (!completado) { setCompletado(true); toast.error('Conexión perdida'); } };
  };

  const verFactura = async (doc) => {
    setPreviewLoading(true);
    try {
      const res = await documentsApi.getById(doc.id);
      setPreviewDoc(res.data?.data || res.data);
    } catch (e) { toast.error('Error al cargar documento'); }
    finally { setPreviewLoading(false); }
  };

  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  const toggleSelectAll = () => {
    const eligible = tabDocs.filter(d => d.apiReviewStatus === 'pending_review' || d.estadoHacienda === 'borrador' || d.apiReviewStatus === 'rejected');
    setSelectedIds(prev => prev.length === eligible.length && eligible.length > 0 ? [] : eligible.map(d => d.id));
  };

  const formatMoney = (amount, currency = 'USD') => {
    const num = parseFloat(amount) || 0;
    return currency === 'USD' ? `$${num.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : `₡${num.toLocaleString('es-CR', { minimumFractionDigits: 2 })}`;
  };

  return (
    <div className="space-y-4">
      {/* Toolbar — search absolute left + botones centrados (hook responsive empuja los botones si traslapan) */}
      <div className="bg-white rounded-lg border p-3 relative">
        {/* Search + filter - left absolute */}
        <div ref={searchRef} className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-2 z-10">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar cliente..." className="pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 w-52 text-sm" />
          </div>
          <MonthRangePicker
            value={filtroFecha}
            onChange={setFiltroFecha}
            onRefresh={fetchData}
          />
        </div>
        {/* Toolbar - center */}
        <div ref={buttonsRef} className="flex items-center justify-center gap-2 flex-wrap">
            <button onClick={() => handleAprobar()} disabled={selectedIds.filter(id => docs.find(d => d.id === id)?.apiReviewStatus === 'pending_review').length === 0 || processing}
              className="flex flex-col items-center justify-center px-3 py-2 rounded-lg text-xs font-medium min-w-[70px] border border-cyan-400 text-cyan-600 hover:bg-cyan-50 disabled:opacity-40">
              <Check className="h-5 w-5 mb-1" />
              <span>Aprobar ({selectedIds.filter(id => docs.find(d => d.id === id)?.apiReviewStatus === 'pending_review').length})</span>
            </button>
            <button onClick={handleFacturar} disabled={approvedDocs.length === 0 || processing}
              className="flex flex-col items-center justify-center px-3 py-2 rounded-lg text-xs font-medium min-w-[70px] border border-green-500 text-green-600 hover:bg-green-50 disabled:opacity-40">
              <Zap className="h-5 w-5 mb-1" />
              <span>Facturar ({approvedDocs.length})</span>
            </button>
            {(() => {
              // Solo habilita si entre los seleccionados hay rechazados aún no eliminados
              const selectedRejected = selectedIds.filter(id => {
                const d = docs.find(x => x.id === id);
                return d?.apiReviewStatus === 'rejected' && !d?.apiDeleted;
              });
              return (
                <button
                  onClick={() => setConfirmDeleteIds(selectedRejected)}
                  disabled={selectedRejected.length === 0 || processing}
                  className="flex flex-col items-center justify-center px-3 py-2 rounded-lg text-xs font-medium min-w-[70px] border border-red-500 text-red-600 hover:bg-red-50 disabled:opacity-40"
                  title="Eliminar documentos rechazados seleccionados"
                >
                  <Trash2 className="h-5 w-5 mb-1" />
                  <span>Eliminar ({selectedRejected.length})</span>
                </button>
              );
            })()}
            <button onClick={() => { fetchData(); toast.success('Actualizado'); }}
              className="flex flex-col items-center justify-center px-3 py-2 rounded-lg text-xs font-medium min-w-[70px] border border-gray-300 text-gray-600 hover:bg-gray-50">
              <RefreshCw className={`h-5 w-5 mb-1 ${loading ? 'animate-spin' : ''}`} />
              <span>Actualizar</span>
            </button>
            <button onClick={() => setShowAlertConfig(true)}
              className="flex flex-col items-center justify-center px-3 py-2 rounded-lg text-xs font-medium min-w-[70px] border border-violet-400 text-violet-600 hover:bg-violet-50"
              title="Configurar alertas por correo">
              <Settings className="h-5 w-5 mb-1" />
              <span>Alertas</span>
            </button>
          </div>
      </div>

      {/* Pestañas: separan las FE válidas (por facturar) de las rechazadas por revisión API */}
      <div className="flex items-center gap-1 border-b">
        <button
          onClick={() => switchTab('validas')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition ${activeTab === 'validas' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Válidas
          <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${activeTab === 'validas' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>{validDocs.length}</span>
        </button>
        <button
          onClick={() => switchTab('rechazadas')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition ${activeTab === 'rechazadas' ? 'border-red-600 text-red-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Rechazadas
          <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${activeTab === 'rechazadas' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}>{rejectedDocs.length}</span>
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        {loading ? (
          <div className="p-8 text-center"><div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto" /></div>
        ) : tabDocs.length === 0 ? (
          <div className="p-8 text-center text-gray-500"><FileText className="h-12 w-12 mx-auto text-gray-300 mb-2" /><p>{activeTab === 'rechazadas' ? 'No hay documentos rechazados' : 'No hay documentos válidos'}</p></div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-3 py-3 w-10"><input type="checkbox" checked={selectedIds.length > 0 && selectedIds.length === tabDocs.filter(d => d.apiReviewStatus === 'pending_review' || d.estadoHacienda === 'borrador' || d.apiReviewStatus === 'rejected').length} onChange={toggleSelectAll} className="rounded" /></th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">Tipo</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">Fecha</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">Cliente</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">Integración</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600">Revisión</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600">Total</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600">Estado</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600">Hacienda</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {tabDocs.map(doc => {
                const isPending = doc.apiReviewStatus === 'pending_review';
                return (
                  <tr key={doc.id} onClick={() => setRevision({ doc })}
                    className={`cursor-pointer hover:bg-gray-50 ${selectedIds.includes(doc.id) ? 'bg-blue-50' : ''}`}>
                    <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                      {(isPending || doc.estadoHacienda === 'borrador') && (
                        <input type="checkbox" checked={selectedIds.includes(doc.id)} onChange={() => toggleSelect(doc.id)} className="rounded" />
                      )}
                    </td>
                    <td className="px-3 py-3"><span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">{doc.tipoDocumento === '01' ? 'FE' : doc.tipoDocumento === '03' ? 'NC' : doc.tipoDocumento}</span></td>
                    <td className="px-3 py-3">{dayjs(doc.fechaEmision).format('DD/MM/YYYY')}</td>
                    <td className="px-3 py-3"><p className="font-medium">{doc.receptorNombre}</p><p className="text-xs text-gray-500">{doc.receptorIdentificacion}</p></td>
                    <td className="px-3 py-3"><span className="text-xs text-gray-500">{doc.apiCredentialName || 'API'}</span></td>
                    <td className="px-3 py-3 text-center">
                      {doc.revisionObservaciones == null ? <span className="text-xs text-gray-400">—</span>
                        : doc.revisionObservaciones === 0
                          ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-emerald-400 text-emerald-700 text-[11px] font-semibold whitespace-nowrap"><Check className="w-3 h-3" />Sin observaciones</span>
                          : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-amber-400 text-amber-700 text-[11px] font-semibold whitespace-nowrap"><AlertTriangle className="w-3 h-3" />{doc.revisionObservaciones} {doc.revisionObservaciones === 1 ? 'observación' : 'observaciones'}</span>}
                    </td>
                    <td className="px-3 py-3 text-right font-bold">{formatMoney(doc.totalComprobante, doc.moneda)}</td>
                    <td className="px-3 py-3 text-center">
                      {(() => {
                        const fe = getEstadoFlujo(doc);
                        const cfg = FLUJO_ESTADO[fe];
                        return (
                          <button onClick={(e) => { e.stopPropagation(); setTrackingDoc(doc); }} title="Ver seguimiento"
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium hover:opacity-80 transition ${cfg.color}`}>
                            {cfg.label}
                            <Clock className="w-3 h-3 opacity-60" />
                          </button>
                        );
                      })()}
                      {doc.apiDeleted && <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700 border border-red-200">Eliminada</span>}
                      {doc.apiReviewStatus === 'rejected' && doc.apiReviewReason && <p className="text-xs text-red-500 mt-1">{doc.apiReviewReason}</p>}
                    </td>
                    <td className="px-3 py-3 text-center">
                      {(() => {
                        const hStatus = HACIENDA_STATUS[doc.estadoHacienda] || HACIENDA_STATUS.borrador;
                        return <span className={`px-2 py-0.5 rounded text-xs font-medium ${hStatus.color}`}>{hStatus.label}</span>;
                      })()}
                    </td>
                    <td className="px-3 py-3 text-center" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => verFactura(doc)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg" title="Ver detalle"
                        >
                          <Eye size={16} />
                        </button>
                        {isPending && (
                          <>
                            <button onClick={() => (doc.revisionObservaciones > 0 ? setRevision({ doc }) : handleAprobar([doc.id]))} disabled={processing} className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg" title="Aprobar"><Check size={16} /></button>
                            <button onClick={() => setRevision({ doc, rechazo: true })} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg" title="Rechazar"><XCircle size={16} /></button>
                          </>
                        )}
                        {doc.apiReviewStatus === 'approved' && doc.estadoHacienda === 'borrador' && (
                          <button
                            onClick={() => setConfirmDesaprobarId(doc.id)}
                            disabled={processing}
                            className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg disabled:opacity-50"
                            title="Desaprobar — vuelve a Pendiente de Revisión (solo si no se ha facturado)"
                          >
                            <Undo2 size={16} />
                          </button>
                        )}
                        {doc.apiDeleted && (
                          <button
                            onClick={() => handleRestore(doc.id)}
                            disabled={processing}
                            className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg disabled:opacity-50"
                            title="Restaurar — deshace la eliminación"
                          >
                            <Undo2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Preview Modal */}
      {previewDoc && (() => {
        const moneda = previewDoc.codigoMoneda || previewDoc.codigo_moneda || 'CRC';
        const receptor = {
          nombre: previewDoc.receptorNombre || previewDoc.receptor_nombre,
          identificacion: previewDoc.receptorIdentificacion || previewDoc.receptor_identificacion,
          tipo_identificacion: previewDoc.receptorTipoIdentificacion || previewDoc.receptor_tipo_identificacion || '02',
          email: previewDoc.receptorEmail || previewDoc.receptor_email,
          telefono_numero: previewDoc.receptorTelefonoNumero || previewDoc.receptor_telefono_numero,
          otras_senas: previewDoc.receptorOtrasSenas || previewDoc.receptor_otras_senas,
          dias_credito: previewDoc.receptorDiasCredito || previewDoc.receptor_dias_credito || 30
        };
        const documento = {
          consecutivo: previewDoc.consecutivo || 'BORRADOR',
          tipoDocumento: previewDoc.tipoDocumento || previewDoc.tipo_documento || '01',
          fechaEmision: previewDoc.fechaEmision || previewDoc.fecha_emision || new Date().toISOString(),
          condicionVenta: previewDoc.condicionVenta || previewDoc.condicion_venta || '01',
          medioPago: previewDoc.medioPago || previewDoc.medio_pago || ['01'],
          codigoMoneda: moneda,
          status: previewDoc.status || 'draft',
          notas: previewDoc.notas || '',
          diasCredito: receptor.dias_credito
        };
        // Usar los valores que YA vienen calculados desde la BD (vía documentService.processLineas)
        // — NO recalcular en el frontend. La BD ya tiene impuestoMonto, montoTotalLinea, etc.
        // correctos considerando exoneración. Solo mapeamos snake_case → camelCase como fallback.
        const lineas = (previewDoc.lineas || []).map((l, idx) => ({
          numeroLinea: idx + 1,
          codigo: l.codigo || '',
          sku: l.sku || l.codigoComercial || l.codigo_comercial || '',
          detalle: l.detalle || '',
          cantidad: Number(l.cantidad) || 1,
          unidadMedida: l.unidadMedida || l.unidad_medida || 'Unid',
          precioUnitario: Number(l.precioUnitario ?? l.precio_unitario) || 0,
          montoTotal: Number(l.montoTotal ?? l.monto_total) || 0,
          descuentoMonto: Number(l.descuentoMonto ?? l.descuento_monto) || 0,
          subtotal: Number(l.subtotal) || 0,
          baseImponible: Number(l.baseImponible ?? l.base_imponible) || 0,
          impuestoTarifa: Number(l.impuestoTarifa ?? l.impuesto_tarifa) || 0,
          impuestoMonto: Number(l.impuestoMonto ?? l.impuesto_monto) || 0,
          impuestoExoneracion: l.impuestoExoneracion || l.impuesto_exoneracion,
          montoTotalLinea: Number(l.montoTotalLinea ?? l.monto_total_linea) || 0
        }));
        // Totales — usar el total_comprobante de la BD si está, sino sumar líneas.
        const totalImpuesto = Number(previewDoc.totalImpuesto ?? previewDoc.total_impuesto)
          || lineas.reduce((s, l) => s + (l.impuestoMonto - (l.impuestoExoneracion?.monto || 0)), 0);
        const totalVenta = Number(previewDoc.totalVentaNeta ?? previewDoc.total_venta_neta)
          || lineas.reduce((s, l) => s + l.subtotal, 0);
        const totalComprobante = Number(previewDoc.totalComprobante ?? previewDoc.total_comprobante)
          || (totalVenta + totalImpuesto);
        const totales = { totalVentaNeta: totalVenta, totalImpuesto, totalComprobante };

        return (
          <DraggableModal title="Vista Previa - Factura API" onClose={() => setPreviewDoc(null)} maxWidth="max-w-4xl">
            <div className="p-4 overflow-auto bg-gray-100" style={{ maxHeight: '80vh' }}>
              <div className="bg-white w-full" style={{ padding: '8mm' }}>
                <InvoiceTemplate
                  documento={documento}
                  emisor={currentEmisor}
                  receptor={receptor}
                  lineas={lineas}
                  totales={totales}
                  esCotizacion={false}
                  tipoCambio={previewDoc.tipoCambio || previewDoc.tipo_cambio || 1}
                  fullWidth={true}
                />
              </div>
            </div>
          </DraggableModal>
        );
      })()}

      {/* Revisión de Finance 360. Mientras se ve la factura queda oculta (sin cerrarse) y vuelve al
          cerrar la vista previa; antes la tapaba porque ambas van en la misma capa */}
      {revision && (
        <div className={previewDoc ? 'hidden' : undefined}>
          <RevisionFacturaApiModal
            doc={revision.doc}
            inicioRechazo={!!revision.rechazo}
            onClose={() => setRevision(null)}
            onCambio={fetchData}
            onVerFactura={verFactura}
          />
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirmarLote}
        onClose={() => setConfirmarLote(null)}
        onConfirm={() => { const ids = confirmarLote.ids; setConfirmarLote(null); handleAprobar(Object.assign([...ids], { confirmado: true })); }}
        title={`${confirmarLote?.conObs || 0} de las seleccionadas tienen observaciones`}
        message="La revisión de Finance 360 encontró observaciones en el catálogo o en el cliente. Puede abrir cada una para revisarla, o aprobarlas igual."
        confirmText="Aprobar igual"
        cancelText="Revisarlas"
        variant="warning"
      />

      {/* Tracking Modal */}
      <TrackingModal doc={trackingDoc} onClose={() => setTrackingDoc(null)} />

      {/* SSE Progress Modal */}
      <ProgresoModal
        isOpen={showProgresoModal}
        onClose={() => { setShowProgresoModal(false); fetchData(); }}
        resultados={resultados}
        progreso={progreso}
        total={totalProcesar}
        stage={stageInfo}
        completado={completado}
      />

      {/* Modal de confirmación: Desaprobar documento aprobado */}
      <ConfirmModal
        isOpen={confirmDesaprobarId !== null}
        onClose={() => setConfirmDesaprobarId(null)}
        onConfirm={confirmarDesaprobar}
        title="¿Desaprobar este documento?"
        message='Volverá a "Pendiente de Revisión" y podrás editarlo o rechazarlo. Solo es posible mientras no se haya facturado (enviado a Hacienda).'
        confirmText="Sí, desaprobar"
        cancelText="No, mantener"
        variant="warning"
      />

      {/* Modal de confirmación: Eliminar documentos rechazados */}
      <ConfirmModal
        isOpen={confirmDeleteIds !== null && confirmDeleteIds.length > 0}
        onClose={() => setConfirmDeleteIds(null)}
        onConfirm={confirmarEliminar}
        title={`¿Eliminar ${confirmDeleteIds?.length || 0} documento(s) rechazado(s)?`}
        message="No se borran de la base de datos: quedan marcados como Eliminada y siguen visibles en la pestaña Rechazadas. Podés restaurarlos cuando quieras."
        confirmText="Sí, eliminar"
        cancelText="No, mantener"
        variant="warning"
      />

      {/* Modal de configuración de alertas */}
      {showAlertConfig && <AlertConfigModal onClose={() => setShowAlertConfig(false)} />}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// AlertConfigModal — config de alertas por correo (inmediata + recordatorio)
// ══════════════════════════════════════════════════════════════════════
function AlertConfigModal({ onClose }) {
  const [recipients, setRecipients] = useState('');
  const [alertArrival, setAlertArrival] = useState(true);
  const [reminderEnabled, setReminderEnabled] = useState(true);
  const [reminderHours, setReminderHours] = useState(4);
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiDraftsApi.getAlertConfig();
        const d = res.data?.data;
        if (d) {
          setRecipients(d.recipient_emails || '');
          setAlertArrival(d.alert_on_arrival !== 0 && d.alert_on_arrival !== false);
          setReminderEnabled(d.reminder_enabled !== 0 && d.reminder_enabled !== false);
          setReminderHours(parseInt(d.reminder_interval_hours, 10) || 4);
          setIsActive(d.is_active !== 0 && d.is_active !== false);
        }
      } catch (e) { /* sin config aún */ }
      finally { setLoading(false); }
    })();
  }, []);

  const count = recipients.split(/[,;\s]+/).map(s => s.trim()).filter(Boolean).length;

  const guardar = async () => {
    setSaving(true);
    try {
      await apiDraftsApi.saveAlertConfig({
        recipientEmails: recipients,
        alertOnArrival: alertArrival,
        reminderEnabled,
        reminderIntervalHours: reminderHours,
        isActive
      });
      toast.success('Configuración guardada');
      onClose();
    } catch (e) { toast.error(e.response?.data?.error || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const enviarPrueba = async () => {
    setSendingTest(true);
    try {
      await apiDraftsApi.saveAlertConfig({
        recipientEmails: recipients,
        alertOnArrival: true,
        reminderEnabled, reminderIntervalHours: reminderHours, isActive: true
      });
      const res = await apiDraftsApi.sendAlertTest();
      toast.success(res.data?.message || 'Prueba enviada');
    } catch (e) { toast.error(e.response?.data?.error || 'Error al enviar prueba'); }
    finally { setSendingTest(false); }
  };

  // Diseño outline: sin rellenos de color, solo el borde cuando la opción está activa
  const opcion = (on) => `rounded-xl border bg-white px-4 py-3 transition-colors ${on ? 'border-indigo-400' : 'border-gray-200'}`;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl mx-4 max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-b-[3px] border-b-violet-500 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl border border-indigo-300 flex items-center justify-center shrink-0">
              <Mail className="h-5 w-5 text-indigo-600" />
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 leading-tight">Alertas de Facturación API</h3>
              <p className="text-xs text-gray-500">Un correo cuando llegan documentos por API y un recordatorio si llevan tiempo sin aprobarse</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 grid md:grid-cols-2 gap-5">
          {/* Columna izquierda: a quién y si está activo */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Destinatarios <span className="text-xs text-gray-400 font-normal">(separados por coma)</span>
              </label>
              <textarea value={recipients} onChange={(e) => setRecipients(e.target.value)} rows={4}
                placeholder="facturacion@empresa.com, gerencia@empresa.com" disabled={loading}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 resize-none" />
              <p className="text-[11px] text-gray-400 mt-1">
                {loading ? 'Cargando…' : `${count} destinatario${count !== 1 ? 's' : ''}`}
              </p>
            </div>
            <label className={`flex items-center gap-3 cursor-pointer ${opcion(isActive)}`}>
              <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="accent-indigo-600 w-4 h-4" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-gray-900">Alertas activas</div>
                <div className="text-xs text-gray-500">Apagado, no se envía ningún correo de esta sección.</div>
              </div>
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${isActive ? 'border-emerald-400 text-emerald-700' : 'border-gray-300 text-gray-500'}`}>{isActive ? 'Activas' : 'Apagadas'}</span>
            </label>
          </div>

          {/* Columna derecha: qué correos */}
          <div className="space-y-4">
            <label className={`flex items-start gap-3 cursor-pointer ${opcion(alertArrival)}`}>
              <input type="checkbox" checked={alertArrival} onChange={(e) => setAlertArrival(e.target.checked)} className="mt-0.5 accent-indigo-600 w-4 h-4" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-gray-900">Alerta inmediata al llegar un documento</div>
                <div className="text-xs text-gray-500">Un correo apenas la API recibe un documento nuevo para revisar.</div>
              </div>
            </label>

            <div className={opcion(reminderEnabled)}>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={reminderEnabled} onChange={(e) => setReminderEnabled(e.target.checked)} className="mt-0.5 accent-indigo-600 w-4 h-4" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-gray-900">Recordatorio de documentos sin aprobar</div>
                  <div className="text-xs text-gray-500">Si hay documentos en «Pendiente de Revisión» por más tiempo del indicado.</div>
                </div>
              </label>
              {reminderEnabled && (
                <div className="mt-3 flex items-center gap-2 pl-7">
                  <label htmlFor="horas-recordatorio" className="text-sm text-gray-700">Cada</label>
                  <input id="horas-recordatorio" type="number" min={1} max={168} value={reminderHours}
                    onChange={(e) => setReminderHours(Math.max(1, parseInt(e.target.value, 10) || 4))}
                    className="w-20 border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400" />
                  <span className="text-sm text-gray-700">horas</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 shrink-0">
          <button onClick={enviarPrueba} disabled={sendingTest || saving || count === 0}
            className="px-4 py-2 border border-indigo-400 text-indigo-700 rounded-lg text-sm font-medium hover:bg-indigo-50 disabled:opacity-50 flex items-center gap-2">
            {sendingTest ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Enviar prueba
          </button>
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium">Cancelar</button>
            <button onClick={guardar} disabled={saving || sendingTest}
              className="px-6 py-2 border border-indigo-500 text-indigo-700 rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50 hover:bg-indigo-50">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Guardar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
